import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CHANGED = "ndg:learning-follow-ups-changed";

export function notifyLearningFollowUpChanged(pupilId) {
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: { pupilId } }));
}

export default function LearningFollowUpSummary({ pupilId, context }) {
  const [loaded, setLoaded] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const sequence = useRef(0);
  useEffect(() => {
    if (!pupilId) return;
    let active = true;
    let controller;
    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      controller?.abort();
      controller = new AbortController();
      const current = ++sequence.current;
      // Do not keep presenting an old question as current during refresh/failure.
      setLoaded(null);
      try {
        const { data } = await axios.get(`${API}/api/learner-framework/pupil/${encodeURIComponent(pupilId)}/learning-follow-ups`, {
          withCredentials: true, signal: controller.signal,
        });
        if (data.pupil_id !== pupilId || typeof data.available !== "boolean" || !Array.isArray(data.items)) {
          throw new Error("Follow-up identity or response unavailable");
        }
        if (active && current === sequence.current) setLoaded({ pupilId, context: data });
      } catch {
        if (active && current === sequence.current) setLoaded({ pupilId, context: { available: false, items: [] } });
      }
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") {
        ++sequence.current; controller?.abort(); setLoaded(null);
      } else refresh();
    };
    const changed = event => { if (event.detail?.pupilId === pupilId) refresh(); };
    refresh();
    // Only mounted, visible preparation summaries poll. No lesson choices,
    // route, recording or prepared briefing are rewritten by these reads.
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener(CHANGED, changed);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      active = false; ++sequence.current; controller?.abort();
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(CHANGED, changed);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [pupilId, attempt]);
  if (!pupilId) return <Summary context={context} />;
  const current = loaded?.pupilId === pupilId ? loaded.context : null;
  return <div>
    {!current && <p className="mt-2 text-sm" role="status">Refreshing learning follow-ups…</p>}
    {current && <Summary context={current} />}
    {current?.available && current.items.length === 0 && <p className="mt-2 text-sm">No current learning follow-ups are listed.</p>}
    <button type="button" className="min-h-11 text-sm underline" onClick={() => setAttempt(value => value + 1)}>Refresh learning follow-ups</button>
  </div>;
}

function Summary({ context }) {
  if (!context) return null;
  if (!context.available) return <p className="mt-2 text-sm" role="status">Learning follow-ups could not be loaded. Check the pupil's learning list when available.</p>;
  const items = (context.items || []).filter(item => item.state !== "withdrawn");
  const next = items.find(item => item.state !== "reviewed" && item.state !== "withdrawn");
  return <div className="mt-3 border-t pt-2 text-sm" data-testid="learning-follow-up-summary">
    {context.counts_complete === false && <p>Some follow-up records are not included here.</p>}
    {next && <>
      <p className="font-semibold">Learning follow-up: {next.focus}</p>
      <p>{next.question}</p>
      {next.original_pupil_answer && <p>Earlier pupil answer: {next.original_pupil_answer}</p>}
      {next.pupil_answer && <p>Pupil answer: {next.pupil_answer}</p>}
      {next.pupil_outcome && <p>Pupil response: {({ understood: "I understand it", practised: "I tried it", need_more_help: "I need more help", not_confident: "I’m still unsure", completed: "Completed" })[next.pupil_outcome] || next.pupil_outcome}</p>}
      {next.state === "needs_instructor_review" ? <p>Awaiting instructor review in the pupil's learning list.</p> : <p>This question is waiting in the pupil's learning to-do list.</p>}
      <p className="mt-1">{next.evidence_required}</p>
    </>}
    {!next && items.length > 0 && <p>The included follow-up responses have been reviewed. This does not confirm driving competence.</p>}
  </div>;
}

